import { useState, useEffect, useRef } from 'react';

const TYPING_SPEED = 80;
const DELETING_SPEED = 50;
const PAUSE_AFTER_TYPING = 2000;
const PAUSE_AFTER_DELETING = 400;

export function useTypewriter(words: string[]) {
  const [displayText, setDisplayText] = useState(words[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const wordIndexRef = useRef(0);
  const initialPauseDone = useRef(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const initialTimeout = setTimeout(() => {
      initialPauseDone.current = true;
      setIsDeleting(true);
    }, PAUSE_AFTER_TYPING);

    return () => clearTimeout(initialTimeout);
  }, []);

  useEffect(() => {
    if (!initialPauseDone.current) return;

    const currentWord = words[wordIndexRef.current];

    let delay: number;

    if (isDeleting) {
      if (displayText.length > 0) {
        delay = DELETING_SPEED;
      } else {
        wordIndexRef.current = (wordIndexRef.current + 1) % words.length;
        setIsDeleting(false);
        delay = PAUSE_AFTER_DELETING;
      }
    } else {
      if (displayText.length < currentWord.length) {
        delay = TYPING_SPEED;
      } else {
        delay = PAUSE_AFTER_TYPING;
      }
    }

    const timeout = setTimeout(() => {
      if (isDeleting) {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          wordIndexRef.current = (wordIndexRef.current + 1) % words.length;
        }
      } else {
        const target = words[wordIndexRef.current];
        if (displayText.length < target.length) {
          setDisplayText(target.slice(0, displayText.length + 1));
        } else {
          setIsDeleting(true);
        }
      }
    }, delay);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, words]);

  return displayText;
}
